import { readinessExplanations } from "../contracts/randomness-readiness";

const STEP_SIZE = 300n;

export interface MintProgress {
  total: bigint;
  stepIndex: bigint;
  stepProgress: number;
  mintsRemaining: number;
  ratio: number;
}

export function deriveMintProgress(cumulativeTotalMinted: string, stepSize = Number(STEP_SIZE)): MintProgress {
  if (!Number.isSafeInteger(stepSize) || stepSize <= 0) throw new Error("Invalid mint fee step size");
  if (!/^(0|[1-9][0-9]*)$/.test(cumulativeTotalMinted)) {
    throw new Error("cumulativeTotalMinted must be a canonical unsigned integer");
  }
  const total = BigInt(cumulativeTotalMinted);
  const stepIndex = total / BigInt(stepSize);
  const boundedProgress = Number(total % BigInt(stepSize));
  return {
    total,
    stepIndex,
    stepProgress: boundedProgress,
    mintsRemaining: stepSize - boundedProgress,
    ratio: boundedProgress / stepSize,
  };
}

export function formatBigInt(value: bigint | string): string {
  const parsed = typeof value === "bigint" ? value : BigInt(value);
  return new Intl.NumberFormat("en-US").format(parsed);
}

/** Display-only USD rounding. Source amounts and eligibility remain untouched. */
export function formatUsd(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return "USD unavailable";
  const [, sign, whole, fraction = ""] = match;
  const cents = BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2))
    + ((fraction[2] ?? "0") >= "5" ? 1n : 0n);
  return `${sign && cents !== 0n ? "-" : ""}$${formatBigInt(cents / 100n)}.${String(cents % 100n).padStart(2, "0")}`;
}

/** Group decimal strings without dropping or rounding any source digits. */
export function formatUsdExact(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return "USD unavailable";
  return `${match[1]}$${formatBigInt(match[2]!)}${match[3] === undefined ? "" : `.${match[3]}`}`;
}

export function formatWeiAsEth(wei: bigint | string, maximumFractionDigits = 6): string {
  const value = typeof wei === "bigint" ? wei : BigInt(wei);
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const whole = absolute / 1_000_000_000_000_000_000n;
  const remainder = absolute % 1_000_000_000_000_000_000n;
  if (remainder === 0n || maximumFractionDigits === 0) return `${negative ? "-" : ""}${whole}`;
  const digits = remainder.toString().padStart(18, "0").slice(0, Math.max(0, maximumFractionDigits));
  const trimmed = digits.replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${trimmed ? `.${trimmed}` : ""}`;
}

export function shortAddress(value: string | null, left = 6, right = 4): string {
  if (!value) return "—";
  if (value.length <= left + right + 1) return value;
  return `${value.slice(0, left)}…${value.slice(-right)}`;
}

export function formatElapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remaining = seconds % 60;

  const time = [hours, minutes, remaining]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");

  return days > 0 ? `${days}d ${time}` : time;
}

export function formatRewardPercent(rewardBps: number | null): string {
  if (rewardBps === null) return "Pending";
  const whole = Math.floor(rewardBps / 100);
  const fractional = String(rewardBps % 100).padStart(2, "0");
  return `${whole}.${fractional}%`;
}

export function explainBlockingReason(reason: string): string {
  const explanations: Record<string, string> = {
    ELYSIUM_MINT_IDENTITY_MISMATCH: "Mint configuration differs from the reviewed Elysium deployment. Refresh and review again.",
    ELYSIUM_MINT_REVIEW_REQUIRED: "Prepare and review an Elysium mint authorization before submitting.",
    NET_BUY_BELOW_MINIMUM: "Your current net buy does not meet the minimum mining requirement.",
    ELYSIUM_DEPLOYMENT_PROVISIONAL: "The Elysium deployment is provisional. Transactions remain unavailable.",
    ELYSIUM_DEPLOYMENT_MISMATCH: "The backend deployment does not match the configured Elysium collection and source token.",
    ELYSIUM_CHAIN_MISMATCH: "The backend is serving a different network from Elysium.",
    ELYSIUM_API_IDENTITY_MISMATCH: "The backend has not confirmed the expected Elysium token and pool.",
    ELYSIUM_API_READ_UNAVAILABLE: "Current Elysium data is unavailable. Wait for a fresh verified read.",
    HYPE_USD_PRICE_UNAVAILABLE: "A trusted HYPE/USD price is not available yet.",
    HYPE_USD_PRICE_STALE: "The HYPE/USD feed is older than the safety limit.",
    KNOWN_ID_ONLY_NOT_WALLET_INVENTORY: "This is a read of one token ID, not your complete wallet inventory.",
    MINT_DISPLAY_CACHE_STALE: "Mint display data has expired. Refresh current data before preparing a mint.",
    MINT_SERVER_TIME_UNAVAILABLE: "Mint data freshness could not be verified. Refresh and try again.",
    MINT_DISPLAY_REFRESH_FAILED: "Mint state could not be refreshed. The displayed fee is stale; try refreshing again later.",
    MINT_DISPLAY_CACHE_UNAVAILABLE: "Mint state is temporarily unavailable. Try refreshing again later.",
    MINT_PREPARATION_UNAVAILABLE: "Mint preparation is currently unavailable. Refresh current data and try again.",
    MINT_PREPARATION_REQUIRED: "Quiver is checked when preparing a mint. A successful preparation is required before wallet confirmation.",
    PROVIDER_CHECK_MODE_UNKNOWN: "The backend provider-check mode is unavailable or unsupported.",
    ETH_USD_PRICE_STALE: "The ETH/USD feed is older than the safety limit.",
    ETH_USD_PRICE_UNAVAILABLE: "A trusted ETH/USD price is not available yet.",
    SCANNER_NOT_CAUGHT_UP: "The trade scanner is still catching up to the confirmed chain head.",
    TOKEN_TRADE_SCAN_IN_PROGRESS: "Token trades are being scanned. Displayed tiers may use the last confirmed snapshot; mint eligibility is checked again during preparation.",
    CHAIN_HEAD_OBSERVATION_STALE: "The latest chain-head observation is stale.",
    CHAIN_HEAD_STALLED: "The observed chain head has stopped advancing.",
    BACKFILL_OR_RECOVERY_IN_PROGRESS: "Canonical history is being verified or rebuilt.",
    UNRESOLVED_TRADE_ATTRIBUTION: "A relevant trade cannot yet be attributed safely.",
    POST_GRADUATION_TRADE_COVERAGE_UNRESOLVED: "Post-graduation trade coverage is incomplete.",
    FINANCIAL_COVERAGE_UNKNOWN: "Financial history has not been fully verified.",
    FINANCIAL_COVERAGE_INCOMPLETE: "Financial history contains an unresolved gap.",
    LAUNCH_STATE_UNVERIFIED: "The token launch state could not be verified.",
    MINING_DISABLED: "Public mining is disabled by the server.",
    MINING_PERMISSION_UNKNOWN: "Mining permission has not been supplied by the backend.",
    MINT_DISABLED: "New minting is disabled by the backend.",
    FINALIZE_DISABLED: "Reward finalization is disabled by the backend.",
    REDEEM_DISABLED: "Redemption is disabled by the backend.",
    MINT_PERMISSION_UNKNOWN: "Mint permission has not been supplied by the backend.",
    FINALIZE_PERMISSION_UNKNOWN: "The backend has not yet supplied permission to finalize rewards.",
    REDEEM_PERMISSION_UNKNOWN: "The backend has not yet supplied permission to redeem NFTs.",
    MAINNET_WRITES_DISABLED: "Mainnet transactions are disabled by the backend.",
    EXECUTION_MODE_DISABLED: "This backend mode does not permit wallet transactions.",
    EXECUTION_PERMISSION_UNKNOWN: "Transaction execution permission could not be established.",
    CONTRACT_NOT_CONFIGURED: "The NFT collection or execution network is not configured consistently.",
    RANDOMNESS_READINESS_UNCONFIRMED: "Randomness readiness has not been verified. Refresh the status before minting.",
    NFT_INDEXER_NOT_CURRENT: "NFT state has not caught up to verified chain history.",
    PRODUCTION_MINT_DISABLED: "Public minting is not enabled.",
    TOKEN_NOT_LAUNCHED: "The production source token has not been launched or registered yet.",
    CONTRACT_NOT_DEPLOYED: "The project NFT contracts are not deployed on the configured chain.",
    BACKEND_UNAVAILABLE: "The connected backend is unavailable.",
    PROVIDER_PENDING: "Production randomness provider approval is pending.",
    NFT_INDEXER_CATCHING_UP: "NFT history is catching up. Reward and pool data are not current yet.",
    REORG_RECOVERY: "Canonical chain history is being recovered. Wait for verified data.",
    RANDOMNESS_HEALTH_UNAVAILABLE: "Randomness readiness could not be verified. Other data may still be available.",
    WALLET_NOT_CONNECTED: "Connect a wallet to load its data.",
    SIGN_IN_REQUIRED: "Sign in to load your canonical inventory.",
  };
  return explanations[reason] ?? (Object.hasOwn(readinessExplanations, reason) ?
    readinessExplanations[reason as keyof typeof readinessExplanations] : reason.replaceAll("_", " ").toLowerCase());
}
